const HIGH_SCHOOL_GRADE_LABELS = {
  HSA: "HS: Algebra",
  HSF: "HS: Functions",
  HSG: "HS: Geometry",
  HSN: "HS: Number & Quantity",
  HSS: "HS: Statistics & Probability",
};

export const getGradeLabel = (grade) => {
  if (grade === undefined || grade === null || grade === "") return "";
  return HIGH_SCHOOL_GRADE_LABELS[grade] || `Grade: ${grade}`;
};

// Ordering vocabulary. Standard grades and teacher grade_levels share one set of
// tokens — K-8 plus the five CCSS high-school categories, each of which is its
// own grade level, peer to K-8 (HSN, HSA, HSF, HSG, HSS). They sort together at
// the end, so "HS" here is a sort bucket, not a grade any dropdown offers.
export const GRADE_ORDER = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "HS"];
export const normalizeGrade = (g) => (String(g ?? "").startsWith("HS") ? "HS" : String(g ?? ""));

// Courses use a third vocabulary: one course per high-school subject ("Alg1",
// "Alg2", "Geo", "Math1", "Algebra1"), and nothing HS-prefixed. So a
// high-school grade matches a course by subject rather than by string equality —
// without this it matches no course and silently lands on the first one.
const HS_COURSE_GRADE = /^(alg|geo|math ?[123]|integrated)/i;
export const courseMatchesGrade = (courseGradeLevel, grade) =>
  (normalizeGrade(grade) === "HS"
    ? HS_COURSE_GRADE.test(String(courseGradeLevel ?? ""))
    : normalizeGrade(courseGradeLevel) === grade);

// The teacher's default grade from their grade_levels list: the lowest known
// grade (a teacher may hold several), returned verbatim so it matches a grade
// dropdown option — "HSN" stays "HSN", since no tree offers a bare "HS". Null
// when none are recognizable, so callers keep their existing default.
export const defaultTeacherGrade = (gradeLevels = []) => {
  const ranked = (gradeLevels || [])
    .filter((g) => GRADE_ORDER.includes(normalizeGrade(g)))
    .sort(
      (a, b) =>
        GRADE_ORDER.indexOf(normalizeGrade(a)) - GRADE_ORDER.indexOf(normalizeGrade(b)) ||
        String(a).localeCompare(String(b))
    );
  return ranked[0] ?? null;
};

// Course order for the pickers: the numbered grades, then the courses that follow
// grade 8. Checked before the digit read — "Accelerated 6" carries a 6 and would
// otherwise rank among the sixth graders. Matched loosely because the value is
// sometimes the course's grade_level token ("Alg1", "Math2") and sometimes its
// display name ("Algebra 1", "Integrated Math 2"). Anything unrecognised ranks last
// and keeps its incoming order.
const COURSE_ORDER = [
  /^acc\w*\D*6/i,
  /^acc\w*\D*7/i,
  /^alg\w*\D*1/i,
  /^alg\w*\D*2/i,
  /^geo/i,
  /^(int\w*\W*)?math\W*1/i,
  /^(int\w*\W*)?math\W*2/i,
  /^(int\w*\W*)?math\W*3/i,
];

export const courseSequenceRank = (label) => {
  const value = String(label ?? "").trim();
  const course = COURSE_ORDER.findIndex((re) => re.test(value));
  if (course !== -1) return 100 + course;
  const digits = (value.match(/\d+/) || [""])[0];
  if (digits !== "") return Number(digits);
  return /\bk\b/i.test(value) ? 0 : 999;
};

// Order a course list for display without mutating the source. Stable: courses that
// rank the same keep the order they arrived in.
export const sortCoursesBySequence = (courses = []) =>
  [...courses]
    .map((c, i) => ({ c, i, rank: courseSequenceRank(c?.grade_level || c?.name) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map(({ c }) => c);

// The educator's active school carries the standards framework id
// (account/educator -> active_school.standards_framework_id): the framework
// identifier for a Texas / Georgia / Florida school, null or absent for a Common
// Core school. Normalized here so every picker asks the same question the same
// way. A missing, null or empty id means Common Core.
export const standardsFrameworkId = (activeSchool) => {
  const id = activeSchool?.standards_framework_id;
  return typeof id === "string" && id !== "" ? id : null;
};

// Short badge text for a state standards framework (TEKS, GSE, B.E.S.T.).
// Prefers the framework name carried by the standards tree once loaded;
// falls back to matching the framework id (the only thing available before
// the tree loads, or if the tree never carries a name). Returns null for
// Common Core schools.
export const frameworkBadgeLabel = (frameworkId, frameworkName = null) => {
  const candidate = String(frameworkName || frameworkId || "").toLowerCase();

  if (!candidate) return null;
  if (candidate.includes("dd2254d7") || candidate.includes("teks") || candidate.includes("texas")) return "TEKS";
  if (candidate.includes("664ca2e4") || candidate.includes("gse") || candidate.includes("georgia")) return "GSE";
  if (candidate.includes("14f159c1") || candidate.includes("b.e.s.t") || candidate.includes("best") || candidate.includes("florida")) return "B.E.S.T.";
  return "STATE";
};

// Formats the breadcrumb string for a search result (grade / domain / cluster names).
export const getResultBreadcrumb = (result) => {
  if (!result) return "";
  const parts = [];
  if (result.grade) {
    const g = String(result.grade);
    const label = HIGH_SCHOOL_GRADE_LABELS[g] || (g.toUpperCase() === "K" ? "Kindergarten" : `Grade ${g}`);
    parts.push(label);
  }
  if (result.domain_name) {
    parts.push(result.domain_name);
  } else if (result.domain_code) {
    parts.push(result.domain_code);
  }
  if (result.cluster_name) {
    parts.push(result.cluster_name);
  } else if (result.cluster_code) {
    parts.push(result.cluster_code);
  }
  return parts.join(" · ");
};

// Only grades with challenge-bearing standards are worth offering in a grade
// dropdown. Shared by the three create flows so their option sets can't drift.
// includeZeroCount mirrors the tree's state-framework rule: standards mapped to
// zero questions stay selectable (and their grades stay offered) instead of
// being hidden, as they already are for Common Core.
export const gradeHasVisibleStandards = (gradeLevel, includeZeroCount = false) =>
  (gradeLevel.domains || []).some((domain) => {
    if (!includeZeroCount && (domain.count === 0 || domain.challenge_count === 0)) return false;
    return (domain.clusters || []).some((cluster) => {
      if (!includeZeroCount && (cluster.count === 0 || cluster.challenge_count === 0)) return false;
      return (cluster.standards || []).some(
        (std) => includeZeroCount || (std.count !== 0 && std.challenge_count !== 0),
      );
    });
  });

export const findGradeAndStandardNameByPrefix = (standards, prefix, shouldReturnOnlyStandard) => {
  let standardName = '';
  let grade = '';
  main_loop:
  for (let i = 0; i < standards?.length; i++) {
    if (standards[i].domains) {
      for (let j = 0; j < standards[i]?.domains?.length; j++) {
        if (standards[i].domains[j].clusters) {
          for (let k = 0; k < standards[i]?.domains[j]?.clusters?.length; k++) {
            if (standards[i].domains[j].clusters[k].standards) {
              for (let l = 0; l < standards[i]?.domains[j]?.clusters[k]?.standards?.length; l++) {
                const standard = standards[i].domains[j].clusters[k].standards[l];
                const standardCode = standard.code;
                if (standardCode === prefix) {
                  standardName = standard.name;
                  grade = standards[i].grade;
                  break main_loop;
                }
              }
            }
          }
        }
      }
    }
  }
  if (standardName && shouldReturnOnlyStandard) {
    return standardName;
  }
  if (standardName && grade) {
    return getGradeLabel(grade) + ', Standard: ' + standardName;
  }
  return '';
};

export const findClusterNameByPrefix = (standards, prefix) => {
  let clusterName = '';
  main_loop:
    for (let i = 0; i < standards?.length; i++) {
      if (standards[i].domains) {
        for (let j = 0; j < standards[i]?.domains?.length; j++) {
          if (standards[i].domains[j].clusters) {
            for (let k = 0; k < standards[i]?.domains[j]?.clusters?.length; k++) {
              const cluster = standards[i].domains[j].clusters[k];
              const clusterCode = cluster.code;
              if (clusterCode === prefix) {
                clusterName = cluster.name;
                break main_loop;
              }
            }
          }
        }
      }
    }
  return clusterName;
};

export const getAllPrefixes = (standards) => {
  const allPrefixes = [];
  for (let i = 0; i < standards?.length; i++) {
    if (standards[i].domains) {
      for (let j = 0; j < standards[i]?.domains?.length; j++) {
        const domain = standards[i].domains[j];
        if (domain.clusters) {
          const domainCode = domain.code;
          if (domainCode) {
            allPrefixes.push(domainCode);
          }
          for (let k = 0; k < domain.clusters.length; k++) {
            const cluster = domain.clusters[k];
            if (cluster.standards) {
              const clusterCode = cluster.code;
              if (clusterCode) {
                allPrefixes.push(clusterCode);
              }
              for (let l = 0; l < cluster.standards.length; l++) {
                const standard = cluster.standards[l];
                const standardCode = standard.code;
                if (standardCode) {
                  allPrefixes.push(standardCode);
                }
              }
            }
          }
        }
      }
    }
  }
  return allPrefixes;
};

export const buildSelectOptions = (standards) => {
  const options = [];
  for (let i = 0; i < standards?.length; i++) {
    options.push({"value": standards[i].grade, "label": getGradeLabel(standards[i].grade)});
    if (standards[i].domains) {
      for (let j = 0; j < standards[i]?.domains?.length; j++) {
        const domainNode = standards[i].domains[j];
        if (domainNode.clusters) {
          const domainCode = domainNode.code;
          if (domainCode) {
            options.push({"value": domainCode, "label": domainCode + ': ' + domainNode.name});
          }
          for (let k = 0; k < domainNode.clusters.length; k++) {
            const clusterNode = domainNode.clusters[k];
            if (clusterNode.standards) {
              const clusterCode = clusterNode.code;
              if (clusterCode) {
                options.push({"value": clusterCode, "label": clusterCode + ': ' + clusterNode.name});
              }
              for (let l = 0; l < clusterNode.standards.length; l++) {
                const standardNode = clusterNode.standards[l];
                const standardCode = standardNode.code;
                if (standardCode) {
                  options.push({"value": standardCode,
                    "label": standardCode + ': ' + standardNode.name});
                }
              }
            }
          }
        }
      }
    }
  }
  return options;
}

export const getGrades = (standards) => {
  const grades = [];
  for (let i = 0; i < standards?.length; i++) {
    grades.push({"value": standards[i].grade, "label": getGradeLabel(standards[i].grade)});
  }
  return grades;
}

export const getDomainsByGradeLevel = (standards, grade) => {
  const domains = [];
  for (let i = 0; i < standards?.length; i++) {
    if (standards[i].grade === grade) {
      for (let j = 0; j < standards[i]?.domains?.length; j++) {
        const domainNode = standards[i].domains[j];
        const domainCode = domainNode.code;
        if (domainCode) {
          domains.push({"value": domainCode, "label": domainCode + ": " + domainNode.name});
        }
      }
    }
  }
  return domains;
}

export const getClustersByDomain = (standards, domain) => {
  const clusters = [];
  for (let i = 0; i < standards?.length; i++) {
    for (let j = 0; j < standards[i]?.domains?.length; j++) {
      const domainNode = standards[i].domains[j];
      const domainCode = domainNode.code;
      if (domainCode === domain) {
        for (let k = 0; k < domainNode.clusters?.length; k++) {
          const clusterNode = domainNode.clusters[k];
          const clusterCode = clusterNode.code;
          if (clusterCode) {
            clusters.push({"value": clusterCode, "label": clusterCode + ": " + clusterNode.name});
          }
        }
      }
    }
  }
  return clusters;
}

export const getStandardsByCluster = (standards, cluster) => {
  const _standards = [];
  for (let i = 0; i < standards?.length; i++) {
    for (let j = 0; j < standards[i]?.domains?.length; j++) {
      for (let k = 0; k < standards[i]?.domains[j]?.clusters?.length; k++) {
        const clusterNode = standards[i].domains[j].clusters[k];
        const clusterCode = clusterNode.code;
        if (clusterCode === cluster) {
          for (let l = 0; l < clusterNode.standards?.length; l++) {
            const standardNode = clusterNode.standards[l];
            const standardCode = standardNode.code;
            if (standardCode) {
              _standards.push({"value": standardCode, "label": standardCode + ": " + standardNode.name});
            }
          }
        }
      }
    }
  }
  return _standards;
}

export const getNodeByStandardPrefix = (standards, prefix) => {
  let node = {};
  main_loop:
    for (let i = 0; i < standards?.length; i++) {
      const currentGrade = standards[i].grade;
      if (prefix === currentGrade) {
        node.grade = currentGrade;
        node.domain = '';
        node.cluster = '';
        node.standard = '';
        break;
      }
      if (standards[i].domains) {
        for (let j = 0; j < standards[i]?.domains?.length; j++) {
          const domainNode = standards[i].domains[j];
          const domainCode = domainNode.code;
          if (prefix === domainCode) {
            node.grade = currentGrade;
            node.domain = {
              name: domainNode.name,
              code: domainCode
            };
            node.cluster = '';
            node.standard = '';
            break main_loop;
          }
          if (domainNode.clusters) {
            for (let k = 0; k < domainNode.clusters.length; k++) {
              const clusterNode = domainNode.clusters[k];
              const clusterCode = clusterNode.code;
              if (prefix === clusterCode) {
                node.grade = currentGrade;
                node.domain = {
                  name: domainNode.name,
                  code: domainCode
                };
                node.cluster = {
                  name: clusterNode.name,
                  code: clusterCode
                };
                node.standard = '';
                break main_loop;
              }
              if (clusterNode.standards) {
                for (let l = 0; l < clusterNode.standards.length; l++) {
                  const standardNode = clusterNode.standards[l];
                  const standardCode = standardNode.code;
                  if (prefix === standardCode) {
                    node.grade = currentGrade;
                    node.domain = {
                      name: domainNode.name,
                      code: domainCode
                    };
                    node.cluster = {
                      name: clusterNode.name,
                      code: clusterCode
                    };
                    node.standard = {
                      name: standardNode.name,
                      code: standardCode
                    };
                    break main_loop;
                  }
                }
              }
            }
          }
        }
      }
    }
    return node;
};

// Relationship lists read nearest-to-the-core-standard first. Prerequisites sit
// below the core standard, so the nearest one is the highest code and they run
// descending; extensions sit above it, so ascending is already nearest-first.
// Every screen that lists these shares the order, so they agree with each other.
// Callers pass items keyed by `code` (map `standard_code` first if needed).
const byCode = (a, b) =>
  (a?.code || "").localeCompare(b?.code || "", undefined, { numeric: true, sensitivity: "base" });

export const sortStandardsByCode = (standards) => [...(standards || [])].sort(byCode);

// Curriculum alignments spell some codes differently from the question filter
// facets, which use the standard table's leaf form (mirrors the server's
// normalize_standard_code): "5.NF.B.4.a" → "5.NF.B.4a", "A-REI.B.3" → "HSA.REI.B.3".
export const leafStandardCode = (code) =>
  String(code ?? "")
    .replace(/^(?:HS)?([AFGNS])-(.+)$/, "HS$1.$2")
    .replace(/(\d)\.([a-z])$/, "$1$2");

export const sortPrerequisites = (standards) => [...(standards || [])].sort((a, b) => byCode(b, a));

// Which picker nodes can back a group: a leaf standard (the tree leaves them with no
// `type`), a cluster, or a domain — each is just an opaque code prefix downstream.
// Whole grades stay unassignable.
//
// Deliberately NOT the same question as "does this carry relationships", which is
// leaf-only: clusters and domains have no prerequisite or extension edges, so they
// are Core-only targets. Keep the two predicates separate.
export const isAssignableTarget = (node) =>
  Boolean(node) && (!node.type || node.type === "cluster" || node.type === "domain");

// The picker tree hides standards with no questions, and hides a cluster once all
// of its standards are hidden. Rebuilding a selection has to apply the same
// filter or the detail panel would count rows the tree never showed. A state
// framework opts out (includeZeroCount), the same rule the tree and the grade
// dropdown apply; the default keeps the Common Core rule.
const visibleStandardsOf = (cluster, includeZeroCount = false) =>
  (cluster?.standards || []).filter(
    (s) => includeZeroCount || (s.count !== 0 && s.challenge_count !== 0),
  );
const visibleClustersOf = (domain, includeZeroCount = false) =>
  (domain?.clusters || []).filter((c) => visibleStandardsOf(c, includeZeroCount).length > 0);

// Rebuild a picker selection from a bare standard/cluster/domain code, shaped
// exactly as StandardNavigationTree emits it on click — so restoring a stored
// code (e.g. a quick group's current standard) lands the page in the same state
// as clicking that node would. Null when the code isn't in the tree.
export const findSelectionByCode = (standards, code, includeZeroCount = false) => {
  if (!code) return null;
  for (const gradeLevel of standards || []) {
    for (const domain of gradeLevel.domains || []) {
      if (domain.code === code) {
        return {
          entity: { type: "domain", code: domain.code, name: domain.name, clusters: visibleClustersOf(domain, includeZeroCount) },
          grade: gradeLevel.grade,
          domain,
          cluster: null,
        };
      }
      for (const cluster of domain.clusters || []) {
        if (cluster.code === code) {
          return {
            entity: { type: "cluster", code: cluster.code, name: cluster.name, standards: visibleStandardsOf(cluster, includeZeroCount) },
            grade: gradeLevel.grade,
            domain,
            cluster,
          };
        }
        // Leaf standards carry no `type` — the tree passes them through as-is.
        const standard = (cluster.standards || []).find((s) => s.code === code);
        if (standard) return { entity: standard, grade: gradeLevel.grade, domain, cluster };
      }
    }
  }
  return null;
};

// Tree-path expansion for a restored code: the grade / domain / cluster keys
// StandardNavigationTree toggles, derived by finding the code in the tree rather
// than by parsing the code string, the string parse assumed CCSS shapes
// (grade = first segment, HS prefixes) and silently expanded nothing for state
// codes like 111.6.b.5.D or MA.6.GR.2.4. Same keys as the parse produced for
// CCSS codes, and the URL-restore forms (grade- / domain- / cluster- prefixed)
// resolve too. Empty object when the code isn't in the tree, the tree simply
// stays collapsed, as it did for unknown codes before.
export const expandedNodesForCode = (standards, code) => {
  if (!code) return {};
  let target = String(code);
  if (target.startsWith("grade-") || target.startsWith("domain-") || target.startsWith("cluster-")) {
    target = target.slice(target.indexOf("-") + 1);
  }
  for (const gradeLevel of standards || []) {
    if (gradeLevel.grade === target) return { [gradeLevel.grade]: true };
    for (const domain of gradeLevel.domains || []) {
      if (domain.code === target) {
        return { [gradeLevel.grade]: true, [domain.code]: true };
      }
      for (const cluster of domain.clusters || []) {
        if (cluster.code === target || (cluster.standards || []).some((s) => s.code === target)) {
          return {
            [gradeLevel.grade]: true,
            [domain.code]: true,
            [cluster.code]: true,
          };
        }
      }
    }
  }
  return {};
};

// The lesson's extension source: its published building_toward alignments, or —
// when the curriculum publishes none (Eureka Math² has almost no
// building_toward rows) — the primary tier of the server's graph-derived
// extension_tiers, reshaped into the alignment shape the flat renderers
// already consume. Deeper tiers are deliberately not surfaced. Matched by
// level (not [0]): the server drops question-less tiers, so the first group
// is not guaranteed to be level 1.
export const buildingTowardOrTier1 = (lesson) => {
  const buildingToward = lesson?.building_toward || [];
  if (buildingToward.length) return buildingToward;
  const tier1 = (lesson?.extension_tiers || []).find((g) => g.level === 1);
  return (tier1?.standards || []).map((s) => ({
    standard_code: s.code,
    standard_name: s.name,
    standard_description: s.description,
    learning_components: s.learning_components,
  }));
};

// Prereq codes the assign flow can target: the published building_on
// alignments, else every graph-derived prerequisite tier — mirroring the
// server's assign-flow fallback (all three prereq tiers are exposed in the
// UI; extensions go through buildingTowardOrTier1, which stops at tier 1).
export const lessonPrereqCodes = (lesson) => {
  const published = (lesson?.building_on || []).map((b) => b.standard_code).filter(Boolean);
  if (published.length) return published;
  return (lesson?.prerequisite_tiers || [])
    .flatMap((g) => (g.standards || []).map((st) => st.code))
    .filter(Boolean);
};
